"use client";

import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { SectionCard } from "@/components/ui/section-card";
import { SummaryCards } from "@/components/ui/summary-cards";
import { AppTable } from "@/components/ui/app-table";
import { useAuthStore } from "@/store/auth-store";
import { apiClient } from "@/lib/api-client";
import { listTracks } from "@/services/tracks-api";
import { fetchAppointments } from "@/services/service-scheduling-api";
import { formatCurrency, formatVehiclePlate } from "@/lib/formatters";
import type { WorkOrder } from "@/types/work-order";
import type { Track } from "@/types/track";
import type { Appointment } from "@/types/appointment";
import { WorkOrderStatus } from "@/types/work-order-status";
import { TrackStatus } from "@/types/track";
import { ServiceExecutionStatus } from "@/types/service-execution-status";

const statusLabel: Record<string, string> = {
  [WorkOrderStatus.Open]: "Aberta",
  [WorkOrderStatus.InProgress]: "Em andamento",
  [WorkOrderStatus.AwaitingParts]: "Aguardando peças",
  [WorkOrderStatus.Ready]: "Pronta",
  [WorkOrderStatus.Delivered]: "Entregue",
};

const statusTone: Record<
  string,
  "default" | "info" | "warning" | "success" | "danger"
> = {
  [WorkOrderStatus.Open]: "default",
  [WorkOrderStatus.InProgress]: "info",
  [WorkOrderStatus.AwaitingParts]: "warning",
  [WorkOrderStatus.Ready]: "success",
  [WorkOrderStatus.Delivered]: "success",
};

const trackStatusLabel: Record<TrackStatus, string> = {
  [TrackStatus.Free]: "Livre",
  [TrackStatus.Busy]: "Ocupada",
  [TrackStatus.WaitingParts]: "Aguard.",
  [TrackStatus.Finished]: "Finalizada",
};

const trackStatusColor: Record<TrackStatus, string> = {
  [TrackStatus.Free]: "#22c55e",
  [TrackStatus.Busy]: "#ef4444",
  [TrackStatus.WaitingParts]: "#d97706",
  [TrackStatus.Finished]: "#6b7280",
};

function resolveDisplayStatus(order: WorkOrder): WorkOrderStatus {
  if (order.executionStatus === ServiceExecutionStatus.Finished)
    return WorkOrderStatus.Ready;
  if (order.executionStatus === ServiceExecutionStatus.WaitingParts)
    return WorkOrderStatus.AwaitingParts;
  if (order.executionStatus === ServiceExecutionStatus.InProgress)
    return WorkOrderStatus.InProgress;
  return order.status;
}

export default function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const today = new Date().toISOString().slice(0, 10);

  const { data: dashboardData, isLoading: ordersLoading } = useQuery({
    queryKey: ["dashboard:orders"],
    queryFn: async () => {
      const res = await apiClient.get<{
        orders: WorkOrder[];
        totals: {
          total: number;
          open: number;
          inService: number;
          awaitingParts: number;
          monthlyRevenue: number;
        };
      }>("/ordens-servico/dashboard-summary");
      return res.data;
    },
  });
  const orders = dashboardData?.orders ?? [];
  const orderTotals = dashboardData?.totals ?? {
    total: 0,
    open: 0,
    inService: 0,
    awaitingParts: 0,
    monthlyRevenue: 0,
  };

  const { data: tracks = [], isLoading: tracksLoading } = useQuery({
    queryKey: ["dashboard:tracks"],
    queryFn: listTracks,
  });

  const { data: todayAppointments = [], isLoading: agendaLoading } = useQuery({
    queryKey: ["dashboard:agenda", today],
    queryFn: () => fetchAppointments({ from: today, to: today }),
  });

  const recentOrders = orders;

  const sortedAppointments = [...todayAppointments]
    .filter((a) => a.status !== "CANCELADO")
    .sort((a, b) => a.startTime.localeCompare(b.startTime));

  const summaryItems = [
    {
      label: "OS Abertas",
      value: String(orderTotals.open),
      hint: `${orderTotals.total} total`,
      tone: "info" as const,
    },
    {
      label: "Veículos em Serviço",
      value: String(orderTotals.inService),
      hint: `${orderTotals.awaitingParts} aguardando peças`,
      tone: "warning" as const,
    },
    {
      label: "Faturamento Mensal",
      value: formatCurrency(orderTotals.monthlyRevenue),
      hint: "Entregas do mês",
      tone: "success" as const,
    },
    {
      label: "Orçamentos Pendentes",
      value: "—",
      hint: "Ver orçamentos",
      tone: "danger" as const,
    },
    {
      label: "Pistas Livres",
      value: String(tracks.filter((t) => t.status === TrackStatus.Free).length),
      hint: `de ${tracks.length} pistas`,
      tone: "default" as const,
    },
  ];

  function StatusBadge({ order }: { order: WorkOrder }) {
    const s = resolveDisplayStatus(order);
    const tone = statusTone[s] ?? "default";
    const cls =
      tone === "info"
        ? "bg-[#f0f9ff] text-[#0284c7] dark:bg-[rgba(56,189,248,0.1)] dark:text-[#38bdf8]"
        : tone === "warning"
          ? "bg-[#fffbeb] text-[#d97706] dark:bg-[rgba(217,119,6,0.1)]"
          : tone === "success"
            ? "bg-[#f0fdf4] text-[#16a34a] dark:bg-[rgba(34,197,94,0.1)] dark:text-[#22c55e]"
            : "bg-[var(--surface-muted)] text-[var(--muted-foreground)]";
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-medium ${cls}`}
      >
        {statusLabel[s] ?? s}
      </span>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Painel geral"
        title={`Bem-vindo, ${user?.fullName.split(" ")[0] ?? ""}!`}
        description={`Visão geral da oficina — ${new Date().toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}`}
      />

      <SummaryCards columns={5} items={summaryItems} />

      <div
        className="grid gap-4 lg:grid-cols-[1fr_340px]"
        style={{ alignItems: "start" }}
      >
        <SectionCard
          title="Ordens de Serviço Recentes"
          description="Últimas movimentações"
          actions={
            <Link
              href="/ordens"
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-[var(--muted-foreground)] transition hover:bg-[var(--surface-muted)] hover:text-[var(--foreground)]"
            >
              Ver todas
            </Link>
          }
        >
          {ordersLoading ? (
            <div className="flex h-40 items-center justify-center text-[var(--muted-foreground)]">
              <Loader2 className="mr-2 animate-spin" size={16} />
              Carregando...
            </div>
          ) : (
            <AppTable.Container>
              <AppTable.Table>
                <thead>
                  <tr>
                    <AppTable.Th>Nº OS</AppTable.Th>
                    <AppTable.Th>Cliente</AppTable.Th>
                    <AppTable.Th>Veículo</AppTable.Th>
                    <AppTable.Th>Status</AppTable.Th>
                    <AppTable.Th className="text-right">Valor</AppTable.Th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.length === 0 ? (
                    <AppTable.EmptyState
                      colSpan={5}
                      title="Nenhuma ordem encontrada"
                    />
                  ) : (
                    recentOrders.map((order) => (
                      <tr
                        key={order.id}
                        className="transition hover:bg-[var(--surface-muted)]"
                      >
                        <AppTable.Td className="whitespace-nowrap font-medium">
                          {order.number}
                        </AppTable.Td>
                        <AppTable.Td>{order.client.name}</AppTable.Td>
                        <AppTable.Td className="text-[var(--muted-foreground)]">
                          {`${order.vehicle.brand} ${order.vehicle.model}`.trim() ||
                            "—"}{" "}
                          <span className="text-[11.5px]">
                            {formatVehiclePlate(order.vehicle.plate)}
                          </span>
                        </AppTable.Td>
                        <AppTable.Td>
                          <StatusBadge order={order} />
                        </AppTable.Td>
                        <AppTable.Td className="text-right text-[var(--muted-foreground)]">
                          {formatCurrency(order.total)}
                        </AppTable.Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </AppTable.Table>
            </AppTable.Container>
          )}
        </SectionCard>

        <div className="flex flex-col gap-4">
          <SectionCard
            title="Pistas / Boxes"
            description="Status em tempo real"
          >
            {tracksLoading ? (
              <div className="flex h-24 items-center justify-center text-[var(--muted-foreground)]">
                <Loader2 className="animate-spin" size={16} />
              </div>
            ) : tracks.length === 0 ? (
              <p className="px-[18px] py-3 text-[13px] text-[var(--muted-foreground)]">
                Nenhuma pista cadastrada.
              </p>
            ) : (
              <div className="flex flex-col gap-[10px] px-[18px] py-3">
                {tracks.map((track: Track) => (
                  <div
                    key={track.id}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <div className="text-[13px] font-medium text-[var(--foreground)]">
                        {track.name}
                      </div>
                      <div className="text-[11.5px] text-[var(--muted-foreground)]">
                        {track.workOrder
                          ? `${track.workOrder.vehicle.brand} ${track.workOrder.vehicle.model}`.trim() ||
                            "—"
                          : "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div
                        className="h-[7px] w-[7px] rounded-full"
                        style={{ background: trackStatusColor[track.status] }}
                      />
                      <span className="text-[12px] text-[var(--muted-foreground)]">
                        {trackStatusLabel[track.status]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Agenda Hoje" description="Próximos agendamentos">
            {agendaLoading ? (
              <div className="flex h-24 items-center justify-center text-[var(--muted-foreground)]">
                <Loader2 className="animate-spin" size={16} />
              </div>
            ) : sortedAppointments.length === 0 ? (
              <p className="px-[18px] py-3 text-[13px] text-[var(--muted-foreground)]">
                Nenhum agendamento para hoje.
              </p>
            ) : (
              <div className="flex flex-col gap-[10px] px-[18px] py-3">
                {sortedAppointments.map((appt: Appointment) => (
                  <div key={appt.id} className="flex items-start gap-3">
                    <div className="min-w-[38px] pt-[1px] text-[11.5px] font-semibold text-[#E8441A]">
                      {appt.startTime.slice(0, 5)}
                    </div>
                    <div>
                      <div className="text-[13px] font-medium text-[var(--foreground)]">
                        {appt.customer.name}
                      </div>
                      <div className="text-[11.5px] text-[var(--muted-foreground)]">
                        {appt.service.name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
